import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/api/apiFetch';

const FULL_HEIGHT_STYLE = {
  top: 'env(safe-area-inset-top)',
  height: 'calc(100% - var(--bottom-nav-height) - env(safe-area-inset-top))',
};

const getAcademyBaseUrl = (): string => import.meta.env.VITE_ACADEMY_URL ?? '';

const fetchAcademyToken = async (): Promise<string | null> => {
  try {
    const res = await apiFetch('/academy/token', { method: 'POST' });
    if (!res.ok) {
      return null;
    }
    const { token } = (await res.json()) as { token: string };
    return token ?? null;
  } catch {
    return null;
  }
};

const buildIframeSrc = (deeplinkPath: string, token: string | null): string => {
  const baseUrl = getAcademyBaseUrl();
  const origin = new URL(baseUrl).origin;
  let url: URL;
  try {
    url = new URL(deeplinkPath, baseUrl);
    if (url.origin !== origin) {
      url = new URL(baseUrl);
    }
  } catch {
    url = new URL(baseUrl);
  }
  if (token) {
    url.searchParams.set('academy_token', token);
  }
  return url.toString();
};

/** Legacy Academy tab: the Academy web app embedded with a short-lived `/academy/token`. */
export const AcademyFrame: FC<{ deeplinkPath: string }> = ({ deeplinkPath }) => {
  const { t } = useTranslation('academy');
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    fetchAcademyToken().then((token) => {
      if (isCancelled) {
        return;
      }
      setIframeSrc(buildIframeSrc(deeplinkPath, token));
    });
    return () => {
      isCancelled = true;
    };
  }, [deeplinkPath]);

  if (!iframeSrc) {
    return (
      <div
        className='fixed inset-0 flex items-center justify-center bg-background'
        style={FULL_HEIGHT_STYLE}
        role='status'
      >
        <span className='text-sm text-muted-foreground'>{t('loading')}</span>
      </div>
    );
  }

  return (
    <iframe
      src={iframeSrc}
      title={t('frame-title')}
      className='fixed inset-0 w-full border-0'
      style={FULL_HEIGHT_STYLE}
      allow='fullscreen'
    />
  );
};
